import React from 'react';
import {
  diabetesTypeOptions,
  formatDiabetesDuration,
  genderOptions
} from '../../lib/domain-data';
import { prepareProfileDetails, ResolvedDoctor } from '../../services/dataService';
import { Gender, ProfileDetails } from '../../types/domain';
import { AvatarPicker } from './AvatarPicker';
import {
  ControlPlaceSelect,
  ControlPlaceSelection,
  emptyControlPlaceSelection,
  resolveControlPlace
} from './ControlPlaceSelect';
import {
  DoctorSelect,
  DoctorSelection,
  doctorSelectionFromProfile,
  emptyDoctorSelection,
  resolveDoctor
} from './DoctorSelect';

/**
 * The FR-02 profile fields as a controlled fieldset (SoW Bagian 2.2).
 *
 * Shared by registration, /complete-profile and the /profile editor, for the
 * same reason SocioEconomicFields is shared: three copies of a form drift, and
 * the one that drifts stops asking for a field. Like that component it renders
 * `.field` markup only — no card, heading or submit button.
 */
export interface ProfileFormValue {
  details: ProfileDetails;
  place: ControlPlaceSelection;
  doctor: DoctorSelection;
}

export const emptyProfileDetails: ProfileDetails = {
  fullName: '',
  birthDate: '',
  gender: null,
  avatarId: null,
  diabetesType: 'Tipe 1',
  medicalRecordNo: null,
  diagnosisDate: ''
};

export const emptyProfileForm: ProfileFormValue = {
  details: emptyProfileDetails,
  place: emptyControlPlaceSelection,
  doctor: emptyDoctorSelection
};

type StoredProfile = {
  full_name?: string | null;
  birth_date?: string | null;
  gender?: string | null;
  avatar_id?: string | null;
  diabetes_type?: string | null;
  medical_record_no?: string | null;
  diagnosis_date?: string | null;
  hospital_id?: string | null;
  control_place?: string | null;
  doctor_id?: string | null;
  doctor_name?: string | null;
};

/** Lifts a profile row into the editor's shape, so an edit is not a re-entry. */
export function profileFormFromProfile(profile: StoredProfile | null): ProfileFormValue {
  if (!profile) {
    return emptyProfileForm;
  }
  const place: ControlPlaceSelection = profile.hospital_id
    ? { kind: 'existing', id: profile.hospital_id, name: profile.control_place ?? '' }
    : profile.control_place
      ? { kind: 'other', name: profile.control_place }
      : emptyControlPlaceSelection;

  return {
    details: {
      // The signup trigger's stand-in for a missing name is not a name.
      fullName: profile.full_name === 'Pengguna baru' ? '' : profile.full_name ?? '',
      birthDate: profile.birth_date ?? '',
      gender: (profile.gender as Gender | null) ?? null,
      avatarId: profile.avatar_id ?? null,
      diabetesType: profile.diabetes_type || 'Tipe 1',
      medicalRecordNo: profile.medical_record_no ?? null,
      diagnosisDate: profile.diagnosis_date ?? ''
    },
    place,
    doctor: doctorSelectionFromProfile(profile)
  };
}

/**
 * Whether every required FR-02 field is stored — drives the "lengkapi profil"
 * nudge on Home.
 *
 * A nudge and not a gate: ProtectedRoute could hold an incomplete profile at
 * /profile, but that would lock a child out of recording an injection because
 * a diagnosis date is missing — and a profile save is not queued offline, so
 * offline it would lock them out entirely.
 */
export function isProfileComplete(profile: StoredProfile | null): boolean {
  if (!profile) {
    return false;
  }
  return (
    !!profile.full_name &&
    profile.full_name !== 'Pengguna baru' &&
    !!profile.birth_date &&
    !!profile.gender &&
    !!profile.avatar_id &&
    !!profile.diabetes_type &&
    !!profile.hospital_id &&
    (!!profile.doctor_id || !!profile.doctor_name) &&
    !!profile.diagnosis_date
  );
}

/**
 * Validates the form and resolves both pickers to what gets stored.
 *
 * Order matters: the fields are checked before the hospital is resolved,
 * because resolving a "Lainnya" hospital creates a pending control_places row
 * — a form that is going to be rejected must not leave one behind.
 */
export async function resolveProfileForm(value: ProfileFormValue): Promise<{
  details: ProfileDetails;
  place: { id: string; name: string };
  doctor: ResolvedDoctor;
}> {
  const details = prepareProfileDetails(value.details);
  if (value.place.kind === 'none') {
    throw new Error('Tempat kontrol belum dipilih.');
  }
  const doctor = resolveDoctor(value.doctor);
  const place = await resolveControlPlace(value.place);
  return { details, place, doctor };
}

interface ProfileFieldsProps {
  value: ProfileFormValue;
  onChange: (value: ProfileFormValue) => void;
  disabled?: boolean;
  /** Prefix for the input ids, so two instances on one page stay unique. */
  idPrefix?: string;
}

export const ProfileFields: React.FC<ProfileFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  idPrefix = 'profile'
}) => {
  const { details } = value;
  const setDetail = <K extends keyof ProfileDetails>(key: K, next: ProfileDetails[K]) =>
    onChange({ ...value, details: { ...details, [key]: next } });

  // A doctor belongs to a hospital, so a new hospital clears the doctor —
  // otherwise the profile could name a doctor who does not work there.
  const setPlace = (place: ControlPlaceSelection) => {
    const sameHospital =
      place.kind === 'existing' &&
      value.place.kind === 'existing' &&
      place.id === value.place.id;
    onChange({ ...value, place, doctor: sameHospital ? value.doctor : emptyDoctorSelection });
  };

  const duration = formatDiabetesDuration(details.diagnosisDate);

  return (
    <div className="field-group">
      <AvatarPicker
        value={details.avatarId}
        onChange={id => setDetail('avatarId', id)}
        disabled={disabled}
        name={`${idPrefix}-avatar`}
      />

      <div className="field">
        <label htmlFor={`${idPrefix}-fullName`}>Nama Lengkap</label>
        <input
          id={`${idPrefix}-fullName`}
          type="text"
          value={details.fullName}
          disabled={disabled}
          onChange={e => setDetail('fullName', e.target.value)}
          required
          placeholder="Nama Lengkap"
        />
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-birthDate`}>Tanggal Lahir</label>
        <input
          id={`${idPrefix}-birthDate`}
          type="date"
          value={details.birthDate}
          disabled={disabled}
          onChange={e => setDetail('birthDate', e.target.value)}
          required
        />
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-gender`}>Jenis Kelamin</label>
        <select
          id={`${idPrefix}-gender`}
          value={details.gender ?? ''}
          disabled={disabled}
          onChange={e => setDetail('gender', (e.target.value || null) as Gender | null)}
          required
        >
          <option value="">Pilih jenis kelamin</option>
          {genderOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-diabetesType`}>Tipe Diabetes</label>
        <select
          id={`${idPrefix}-diabetesType`}
          value={details.diabetesType}
          disabled={disabled}
          onChange={e => setDetail('diabetesType', e.target.value)}
        >
          {diabetesTypeOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-diagnosisDate`}>Tanggal Diagnosis</label>
        <input
          id={`${idPrefix}-diagnosisDate`}
          type="date"
          value={details.diagnosisDate}
          min={details.birthDate || undefined}
          disabled={disabled}
          onChange={e => setDetail('diagnosisDate', e.target.value)}
          required
        />
        {duration ? (
          <div className="subtitle" style={{ marginTop: 4, fontSize: 12 }}>
            Lama menderita DM: {duration}
          </div>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-mrn`}>Nomor Rekam Medis (opsional)</label>
        <input
          id={`${idPrefix}-mrn`}
          type="text"
          value={details.medicalRecordNo ?? ''}
          disabled={disabled}
          maxLength={30}
          onChange={e => setDetail('medicalRecordNo', e.target.value || null)}
          placeholder="Lihat kartu berobat"
        />
      </div>

      {/* Hospital and doctor sit side by side in the two-column layout: the
          doctor list is the one the hospital choice loads. */}
      <ControlPlaceSelect
        id={`${idPrefix}-controlPlace`}
        value={value.place}
        onChange={setPlace}
      />

      <DoctorSelect
        id={`${idPrefix}-doctor`}
        placeId={value.place.kind === 'existing' ? value.place.id : null}
        value={value.doctor}
        onChange={doctor => onChange({ ...value, doctor })}
        disabled={disabled}
      />
    </div>
  );
};

export default ProfileFields;
